import React, { FC, useEffect, useMemo, useRef, useState, memo, useCallback } from 'react';

import {
    FilterOutlined,
    PercentageOutlined,
    CloseOutlined,
    PushpinOutlined,
    VerticalRightOutlined,
    VerticalLeftOutlined,
    QuestionCircleOutlined, EditOutlined,
    CopyOutlined,
    BgColorsOutlined,
} from '@ant-design/icons';

const ChartLineIcon = () => (
    <span role="img" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="square" strokeWidth="2">
            <path d="M21 21H3V3" /><path d="m20.5 8l-5 5l-4-4l-5 5" />
        </svg>
    </span>
);

const CumulativeChartIcon = () => (
    <span role="img" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24">
            <path fill="currentColor" d="M4 19v1h18v2H2V2h2v15c3 0 6-2 8.1-5.6c3-5 6.3-7.4 9.9-7.4v2c-2.8 0-5.5 2.1-8.1 6.5C11.3 16.6 7.7 19 4 19" />
        </svg>
    </span>
);

const TrendLinearIcon = () => (
    <span role="img" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none">
            <path d="M21 7.5a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0M8.25 15.798a1.5 1.5 0 1 1-1.5-2.598a1.5 1.5 0 0 1 1.5 2.598" />
            <path stroke="currentColor" strokeLinecap="square" strokeWidth="2" d="M21 21H3V3" />
            <path stroke="currentColor" strokeLinecap="square" strokeWidth="2" d="m8.8 13.75l9.4-5.49M21 7.5a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0ZM8.25 15.798a1.5 1.5 0 1 1-1.5-2.598a1.5 1.5 0 0 1 1.5 2.598Z" />
        </svg>
    </span>
);

const TrendMaIcon = () => (
    <span role="img" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="square" strokeWidth="2">
            <path d="M21 21H3V3" />
            <path d="M6.228 13.447s2.727-5.376 5.25-4.661c2.521.715 1.543 3.742 4.032 4.478c2.49.737 5.234-4.648 5.234-4.648" />
        </svg>
    </span>
);

const TrendAvgIcon = () => (
    <span role="img" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="square" strokeWidth="2">
            <path d="M21 21H3V3" />
            <path d="M19 6.5c0-1.71-1.343-3.096-3-3.096S13 4.79 13 6.5v2m-6 6.07C7 16.465 8.343 18 10 18s3-1.535 3-3.429V12.5m3-1.998h-.004v-.004H16zm3.004 0H19v-.004h.004zm-9.004 0h-.004v-.004H10zM7.004 10.5H7v-.004h.004zm6 .002H13v-.004h.004z" />
        </svg>
    </span>
);

import { Button, Input, InputNumber, Popover, Radio, Space, Tooltip } from 'antd';
import classNames from 'classnames';

import '../../../../../../app/styles/main.scss';
import { TrendType } from '../../../../../../app/providers/redux/slices/reports/chart/types';
import { isNumeric } from '../../../../../../helpers/utils/functions';
import type { CellFillSettings } from '../../../../../../helpers/utils/tableHelpers';
import { showErrorMessage, showSuccessMessage } from '../../../../../../helpers/utils/ui';
import { deleteCustomMetricWithConfirm } from '../../../../../../helpers/utils/metricsDelete';
import { unApi } from '../../../../api/endpoints/reports/unApi';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import { customIcons } from '../../../Icons/customIcons';
import CreateFromExistingMetricModal from '../../CreateFromExistingMetricModal';
import EditMetricsModal from '../../EditMetricsModal';
import FilterTag from '../../FilterTag';
import { declineCumulativeMetrics } from '../../modal-settings/index';
import MetricsSelectModal from '../../chart/components/bubble-chart/MetricsSelectModal';
import { SparklineIcon } from '../../report-table/Sparkline';
import CellFillModal from './CellFillModal';
import { getMetricDataKey, hasMetricByDataKey } from '../../metrics/utils';

// ==================== Типы ====================

/**
 * Значение фильтра для колонки
 */
interface FilterValue {
    value: number | null;
    action: 'less' | 'equal' | 'over';
    default?: boolean;
}

/**
 * Карта фильтров по колонкам
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FiltersMap = Record<string, any>;

/**
 * Карта сортировки
 */
interface SortMap {
    [dataKey: string]: 'ASC' | 'DESC';
}

/**
 * Карта закреплённых колонок
 */
interface FrozensMap {
    [dataKey: string]: 'left' | 'right' | boolean;
}

/**
 * Данные колонки для заголовка
 */
interface ColumnData {
    dataKey: string;
    title: string;
    description?: string;
    background?: string;
    plan?: number;
    base_metric_name?: string;
    metric_formula?: string;
    unit?: string;
}

/**
 * Элемент выбранной метрики
 */
interface MetricCheckedItem {
    name: string;
    label: string;
    plan?: number | null;
    base_metric_name?: string;
    metric_formula?: string;
}

/**
 * Props для поповера фильтра
 */
interface FilterPopoverProps {
    filters: FiltersMap;
    dataKey: string;
    setVisible: (visible: boolean) => void;
    setVisibleFilterColumn: React.Dispatch<React.SetStateAction<boolean>>;
    blockId?: string;
}

/**
 * Props для поповера закрепления
 */
interface PinPopoverProps {
    dataKey: string;
    setVisible: (visible: boolean) => void;
    setFrozens: React.Dispatch<React.SetStateAction<FrozensMap>>;
}

/**
 * Props компонента HeaderCell
 */
interface HeaderCellProps {
    column: ColumnData;
    filters: FiltersMap;
    sort: SortMap;
    setFrozens: React.Dispatch<React.SetStateAction<FrozensMap>>;
    disabled?: boolean;
    item?: any;
    hideMetricButtons?: boolean;
    /** Compact-режим (дашборд): три точки переносятся в заголовок после (?) */
    isCompactMode?: boolean;
    /** ID блока дашборда — для dispatch в block-specific state */
    blockId?: string;
    /** Окно блока-таблицы дашборда: при hideMetricButtons оставить спарклайн и «…» → «Дополнительно» */
    showTableSettings?: boolean;
}

// ==================== Компоненты ====================

const FilterPopover: FC<FilterPopoverProps> = ({ filters, dataKey, setVisible, setVisibleFilterColumn, blockId }) => {

    const [ value, setValue ] = useState(() => {
        if (!filters[dataKey]) return null;
        return filters[dataKey].value;
    });
    const [ action, setAction ] = useState(() => {
        if (!filters[dataKey]) return 'equal';
        return filters[dataKey].action;
    });

    const refInput = useRef<HTMLInputElement>(null);

    const { setFiltersTable } = useActions();
    const dispatchFilters = (newFilters: FiltersMap) => {
        if (blockId) {
            setFiltersTable({ blockId, data: newFilters });
        } else {
            setFiltersTable(newFilters);
        }
    };
    const handleApply = () => {
        let newFilters = { ...filters };
        newFilters[dataKey] = { value, action };
        dispatchFilters(newFilters);
        setVisibleFilterColumn((prev) => prev ? prev : !prev);
        setVisible(false);
    };
    const handleInputChange = (num: number | null) => {
        if (num && Number.isInteger(num) && action === 'equal') {
            setValue(parseFloat(num.toFixed(2)));
        } else {
            setValue(num);
        }
    };
    const resetFilter = () => {
        setValue(null);
        setAction('equal');

        let newFilters = { ...filters };
        delete newFilters[dataKey];

        dispatchFilters(newFilters);
        setVisible(false);
    };

    return (
        <div>
            <Space direction="vertical">
                <Radio.Group value={action} onChange={(e) => setAction(e.target.value)}>
                    <Space direction="vertical">
                        <Radio value="less">Меньше</Radio>
                        <Radio value="equal">Равно</Radio>
                        <Radio value="over">Больше</Radio>
                    </Space>
                </Radio.Group>
                <Input.Group compact>
                    <InputNumber
                        ref={refInput}
                        value={value}
                        onChange={handleInputChange}
                        onPressEnter={handleApply}
                        step={1}
                        style={{ width: 78 }}
                    />
                    <Button onClick={resetFilter} icon={<CloseOutlined />} />
                </Input.Group>

                <Button onClick={handleApply} title="Применить (Enter)">
                    Применить
                </Button>
            </Space>
        </div>
    );
};

const PinPopover: FC<PinPopoverProps> = ({ dataKey, setVisible, setFrozens }) => {
    const handlePinLeft = useCallback(() => {
        setFrozens((prev) => ({
            ...prev,
            [dataKey]: 'left'
        }));
        setVisible(false);
    }, [dataKey, setFrozens, setVisible]);

    const handlePinRight = useCallback(() => {
        setFrozens((prev) => ({
            ...prev,
            [dataKey]: 'right'
        }));
        setVisible(false);
    }, [dataKey, setFrozens, setVisible]);

    const handlePinReset = useCallback(() => {
        setFrozens((prev) => {
            const newData = { ...prev };
            delete newData[dataKey];
            return newData;
        });
        setVisible(false);
    }, [dataKey, setFrozens, setVisible]);

    return (
        <div>
            <Space size="small">
                <Button onClick={handlePinLeft} icon={<VerticalRightOutlined />} size="small" title="Закрепить слева" />
                <Button onClick={handlePinReset} icon={<CloseOutlined />} size="small" title="Сбросить закрепление" />
                <Button onClick={handlePinRight} icon={<VerticalLeftOutlined />} size="small" title="Закрепить справа" />
            </Space>
        </div>
    );
};

const HeaderCell: FC<HeaderCellProps> = ({ column, filters, sort, setFrozens, disabled = false, hideMetricButtons = false, isCompactMode = false, blockId, showTableSettings = false }) => {
    // Блок-таблица без графика: из кнопок столбца нужны только настройки ячеек
    const onlyTableSettings = hideMetricButtons && showTableSettings;
    const { dataKey, title, description } = column;
    const [ editMetricsState,setEditMetricsState ] = useState<{ visible:boolean,id:any,type:string| null }>({
        visible:false,
        id:dataKey.replace(/_(\d+?)_/g, '$1'),
        type:null
    });

    const [ creatingMetricState, setCreatingMetricState ] = useState<{ visible: boolean; id: null }>({
        visible: false,
        id: null
    });

    const { selectedSchool } = useTypedSelector((state)=>state.currentSchool);
    const [ visibleFilter, setVisibleFilter ] = useState(false);
    const [ visiblePin, setVisiblePin ] = useState(false);
    const [ visibleMetricSelect, setVisibleMetricSelect ] = useState(false);
    const [ visibleShowChart,setVisibleShowChart ] = useState(true);
    const [ visibleFilterColumn,setVisibleFilterColumn ] = useState(false);
    const [ visibleTargetMetric,setVisibleTargetMetric ] = useState(false);
    const [ visibleAbsoluteValue,setVisibleAbsoluteValue ] = useState(false);
    const [ visibleCumulativeChart,setVisibleCumulativeChart ] = useState(false);
    const [ visiblePinFilter,setVisiblePinFilter ] = useState(false);
    const [ visibleMorePopovers, setVisibleMorePopovers ] = useState(false);
    const [ checkedMetric, setCheckedMetric ] = useState(false);
    const [ visibleCellFill, setVisibleCellFill ] = useState(false);
    const { chartType, defaultTrendType, showTrend: globalShowTrend } = useTypedSelector((state) => {
        if (blockId) return state.chart.blocks[blockId] ?? state.chart;
        return state.chart;
    });
    const { percentages, sparklines, cellFills, metricsChecked, metricsLimit,groupsChecked,metrics } = useTypedSelector((state) => {
        if (blockId) return state.table.blocks[blockId] ?? state.table;
        return state.table;
    });
    const { isCompare } = useTypedSelector((state) => state.meta);
    const { isChartVisible } = useTypedSelector((state)=>state.chart);
    const {
        setCumulativeChart,
        setMetricsChecked,
        setMetaChartFetching,
        setMetaCompareChartFetching,
        setMetaChartLoading,
        setSortTable,
        setPercentages,
        setSparklines,
        setCellFills,
        setFiltersTable,
        setChartVisible,
        getCreatedMetrics,
        setMetrics,
        setIsNotParamsFilterVisible,
        setAllDataFetching,
        toggleMetricTrend,
        toggleMetricLine,
        setMetricTrendType,
        setShowTrend,
    } = useActions();

    // Получаем настройки текущей метрики из metricsChecked
    const currentMetricSettings = useMemo(() => {
        const metric = metricsChecked.find((m: any) =>
            m.dataKey === dataKey || m.name === dataKey
        );
        return {
            // undefined = следует за глобальным, true = включён, false = выключен
            showTrend: metric?.showTrend as boolean | undefined,
            hideLine: metric?.hideLine === true,
            trendType: (metric?.trendType as TrendType) || undefined,
        };
    }, [metricsChecked, dataKey]);
    useEffect(()=>{
        if (editMetricsState.visible) {
            // При открытии устанавливаем сразу
            setIsNotParamsFilterVisible(true);
        } else {
            // При закрытии делаем задержку, чтобы дать время cleanup'у EditMetricsModal завершиться
            // и избежать перезагрузки отчёта при восстановлении фильтров
            setTimeout(() => setIsNotParamsFilterVisible(false), 300);
        }
        // Перезагружаем данные только при type === 'save' (изменение структуры метрики)
        // type === 'save_visual' означает изменение только визуальных параметров — перезагрузка не нужна
        if(editMetricsState.type === 'save'){
            setAllDataFetching({ value:true });
        }
    },[ editMetricsState.visible ]);
    const isFiltered = filters[dataKey] && 'primary' && !filters[dataKey]?.default;
    const isPercentage = percentages.includes(dataKey) ? 'primary' : 'default';

    const cumulativeDataKey = `${dataKey?.startsWith('_') ? dataKey?.slice(0,-1) : dataKey}_cumulative`;

    useEffect(() => {
        const _val = hasMetricByDataKey(metricsChecked, dataKey);
        setCheckedMetric(_val);
    }, [metricsChecked, dataKey]);

    const displayCumulativeChart = () => {

        if (cumulativeCheckedMetric) {
            setCumulativeChart(false);
            setMetricsChecked({ data:[ ...metricsChecked.filter((item) => getMetricDataKey(item) !== cumulativeDataKey) ] });
        } else {
            setCumulativeChart(true);
            // Для накопительной метрики используем base_metric_name = dataKey основной метрики,
            // чтобы накопительный график отображался на той же оси, что и основной
            const baseMetricName = column?.base_metric_name || dataKey;
            setMetricsChecked({
                data:[
                    ...metricsChecked.filter((item) => getMetricDataKey(item) !== dataKey),
                    {
                        dataKey: cumulativeDataKey,
                        name: cumulativeDataKey,
                        label: title,
                        plan: visibleTargetMetric ? column?.plan : null,
                        base_metric_name: baseMetricName,
                        metric_formula: column?.metric_formula,
                        unit: column?.unit
                    }
                ]
            });
        }

        setTimeout(() => {
            setMetaChartFetching({ value:true });
            setMetaCompareChartFetching({ value:true });
        }, 50);
    };

    const handleSort = () => {
        if (disabled) return;

        const newSort: SortMap = {};
        if (sort[dataKey] === 'ASC') newSort[dataKey] = 'DESC';
        else newSort[dataKey] = 'ASC';

        const sortData = sort[dataKey] === 'DESC' ? {} : newSort;
        if (blockId) {
            setSortTable({ blockId, data: sortData });
        } else {
            setSortTable(sortData);
        }
    };

    const handlePercentages = () => {
        const newPercentages = percentages.includes(dataKey)
            ? percentages.filter((item: string) => item !== dataKey)
            : [ ...percentages, dataKey ];
        if (blockId) {
            setPercentages({ blockId, data: newPercentages });
        } else {
            setPercentages(newPercentages);
        }
    };

    // Спарклайн (мини-график по периодам) во всех ячейках колонки — вкл/выкл
    const currentSparklines: string[] = Array.isArray(sparklines) ? sparklines : [];
    const isSparkline = currentSparklines.includes(dataKey);
    const handleSparkline = () => {
        const newSparklines = isSparkline
            ? currentSparklines.filter((item) => item !== dataKey)
            : [ ...currentSparklines, dataKey ];
        if (blockId) {
            setSparklines({ blockId, data: newSparklines });
        } else {
            setSparklines(newSparklines);
        }
    };

    // Заливка ячеек обычной метрики по условиям «от – до» (у кастомных — в редакторе метрики)
    const isCustomMetric = dataKey.startsWith('_');
    const currentCellFills: Record<string, CellFillSettings> =
        cellFills && typeof cellFills === 'object' && !Array.isArray(cellFills) ? cellFills : {};
    const cellFill = currentCellFills[dataKey];
    const hasCellFill = Boolean(cellFill?.fillingConditions?.length);
    const handleCellFillSave = (settings: CellFillSettings | null) => {
        const next = { ...currentCellFills };
        if (settings) {
            next[dataKey] = settings;
        } else {
            delete next[dataKey];
        }
        if (blockId) {
            setCellFills({ blockId, data: next });
        } else {
            setCellFills(next);
        }
        setVisibleCellFill(false);
    };

    const cumulativeCheckedMetric =
        useMemo(() => hasMetricByDataKey(metricsChecked, cumulativeDataKey), [metricsChecked, cumulativeDataKey]);
    const hideCumulativeMetric =
        useMemo(() => !!declineCumulativeMetrics.find((item) => item === dataKey), [dataKey]);

    /**
     * Обработчик кнопки основного графика:
     * - Если метрика не выбрана → добавить метрику и показать основной график
     * - Если метрика выбрана и график виден → скрыть основной график
     * - Если метрика выбрана и график скрыт → показать основной график
     */
    const handleCheckedMetric = () => {
        if(!isChartVisible){
            localStorage.setItem('isChartVisible',`${!isChartVisible}`);
            setChartVisible();
        }
        
        if (!checkedMetric) {
            // Добавляем метрику на график
            if (metricsLimit) {
                groupsChecked.length === 1
                    ? setMetricsChecked({ data:[ ...metricsChecked, { dataKey, name: dataKey, label: title, base_metric_name: column?.base_metric_name, metric_formula: column?.metric_formula, unit: column?.unit } ] })
                    : setMetricsChecked({ data:[ { dataKey, name: dataKey, label: title, base_metric_name: column?.base_metric_name, metric_formula: column?.metric_formula, unit: column?.unit } ] });
            } else if ([ 'pie', 'area' ].includes(chartType)) {
                setMetricsChecked({ data:[ { dataKey, name: dataKey, label: title, base_metric_name: column?.base_metric_name, metric_formula: column?.metric_formula, unit: column?.unit } ] });
            } else {
                if (cumulativeCheckedMetric) {
                    setCumulativeChart(false);
                    setVisibleCumulativeChart(false);
                    setMetricsChecked({
                        data:[
                            ...metricsChecked.filter((item) => getMetricDataKey(item) !== cumulativeDataKey),
                            { dataKey, name: dataKey, label: title, base_metric_name: column?.base_metric_name, metric_formula: column?.metric_formula, unit: column?.unit, showTrend: false }
                        ]
                    });
                } else {
                    setMetricsChecked({
                        data:[
                            ...metricsChecked,
                            {
                                dataKey,
                                name: dataKey,
                                label: title,
                                plan: visibleTargetMetric ? column?.plan : null,
                                base_metric_name: column?.base_metric_name,
                                metric_formula: column?.metric_formula,
                                unit: column?.unit
                            }
                        ]
                    });
                }
            }
            
            // Загружаем данные для нового графика
            if (!['treemap', 'bar', 'pie'].includes(chartType)) {
                setMetaChartLoading({ value: true });
                setTimeout(() => {
                    setMetaChartFetching({ value:true });
                    setMetaCompareChartFetching({ value:true });
                }, 50);
            }
        } else if (chartType === 'line' || chartType === 'area') {
            // Линейные графики: переключаем видимость линии (тренд метрики может остаться).
            // Если возвращаем линию, а строк группировки выбрано больше одной — соблюдаем
            // правило графика «несколько строк ↔ одна метрика»: линии остальных метрик гасим.
            if (currentMetricSettings.hideLine && groupsChecked.length > 1) {
                setMetricsChecked({
                    data: metricsChecked.map((item) => (
                        getMetricDataKey(item) === dataKey
                            ? { ...item, hideLine: false }
                            : { ...item, hideLine: true }
                    )),
                });
            } else {
                toggleMetricLine({ dataKey, blockId });
            }
        } else {
            // Остальные типы (воронка, столбцы, круговая, treemap, спидометр) не имеют
            // понятия «линия»/«тренд» и игнорируют флаг hideLine. Чтобы повторный клик
            // действительно убирал показатель с графика, удаляем метрику из выбранных.
            setMetricsChecked({ data: metricsChecked.filter((item) => getMetricDataKey(item) !== dataKey) });
        }
    };
    
    /**
     * Обработчик кнопки тренда:
     * - Если тренд выключен → включить тренд
     * - Если тренд включён и основной график виден → выключить тренд
     * - Если тренд включён и основной график скрыт → выключить тренд и убрать метрику
     */
    const handleToggleTrend = () => {
        if (!currentMetricSettings.showTrend) {
            // Тренд выключен — включаем
            toggleMetricTrend({ dataKey, blockId });
        } else if (!currentMetricSettings.hideLine) {
            // Тренд включён, основной график виден — просто выключаем тренд
            toggleMetricTrend({ dataKey, blockId });
        } else {
            // Тренд включён, основной график скрыт — выключаем тренд и убираем метрику
            setMetricsChecked({ data: metricsChecked.filter((item) => getMetricDataKey(item) !== dataKey) });
        }
    };

    // Эффективный тип тренда: per-metric или глобальный
    const effectiveTrendType = currentMetricSettings.trendType || defaultTrendType || 'linear';

    /**
     * Обработчик кнопок-радио тренда:
     * - Клик на активный тип → выключить тренд
     * - Клик на неактивный тип → включить тренд с этим типом
     */
    // Тренд метрики активен: per-metric true, или undefined (следует за глобальным) и глобальный включён
    const isMetricTrendActive = currentMetricSettings.showTrend === true
        || (currentMetricSettings.showTrend === undefined && globalShowTrend);

    const handleTrendTypeClick = (type: TrendType) => {
        const isCurrentType = type === effectiveTrendType || (type === 'ma' && effectiveTrendType.startsWith('ma'));

        // Включаем глобальный переключатель трендов, если выключен
        if (!globalShowTrend) {
            if (blockId) {
                setShowTrend({ data: true, blockId });
            } else {
                setShowTrend(true);
            }
        }

        if (isMetricTrendActive && isCurrentType) {
            // Выключаем тренд для этой метрики
            toggleMetricTrend({ dataKey, blockId });
        } else {
            if (!isMetricTrendActive) {
                toggleMetricTrend({ dataKey, blockId });
            }
            setMetricTrendType({ dataKey, trendType: type, blockId });
        }
        setVisibleMorePopovers(false);
    };

    const isTrendButtonActive = (type: TrendType) => {
        if (!globalShowTrend) return false;
        if (!isMetricTrendActive) return false;
        if (type === 'ma') return effectiveTrendType.startsWith('ma');
        return effectiveTrendType === type;
    };

    const onRemoveFilter = () => {
        let newFilters = { ...filters };
        delete newFilters[dataKey];

        if (blockId) {
            setFiltersTable({ blockId, data: newFilters });
        } else {
            setFiltersTable(newFilters);
        }
    };
    useEffect(()=>{
        if(!isFiltered){
            setVisibleFilterColumn(false);
        }
    },[ isFiltered ]);
    const tableHeaderCellRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (tableHeaderCellRef.current) {
            if (tableHeaderCellRef.current && tableHeaderCellRef.current.parentElement && column?.background) {
                tableHeaderCellRef.current.parentElement.style.backgroundColor = column?.background;
            }
        }
    },);
    const confirmCreatedMetricsRemoving = useCallback(async (nodeData: { id: number; dataKey: string }) => {
        try {
            await deleteCustomMetricWithConfirm(nodeData.id, location.pathname, {
                onDeleted: (deletedIds) => {
                    const deletedTokens = new Set(deletedIds.map((id) => `_${id}_`));
                    if (selectedSchool?.id) {
                        // Передаём refetch: true чтобы сбросить кэш и получить актуальные данные
                        setTimeout(() => getCreatedMetrics(selectedSchool.id, location.pathname, undefined, true), 150);
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setMetrics(metrics.filter((el: any) => !deletedTokens.has(el.dataKey)));
                    setEditMetricsState(prev => ({ ...prev, visible: false }));
                    showSuccessMessage('Метрика успешно удалена.');
                },
            });
        } catch (err) {
            showErrorMessage('Произошла ошибка во время удаления метрики.');
        }
    }, [selectedSchool?.id, getCreatedMetrics, metrics, setMetrics]);

    const handleDuplicateButtonClick = () => {
        setVisibleMorePopovers(false);
        setCreatingMetricState(prev=>({ ...prev,visible:true }));
    };


    return (
        <div className="table-header-cell" ref={tableHeaderCellRef}>
            <div onClick={handleSort}>
                <div
                    className={classNames('table-header-sort', 'table-header-withIcon', {
                        'sort-asc': sort?.[dataKey] === 'ASC',
                        'sort-desc': sort[dataKey] === 'DESC',
                    })}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            width: '100%'
                        }}
                        className="header-cell_w-icon"
                    >
                        {
                            description && description.length > 1 && <span
                                style={{ marginRight: 5, marginLeft: 5 }}
                            >
                                  <Tooltip
                                      placement="bottom"
                                      title={<div className="page-title-tooltip-text" dangerouslySetInnerHTML={{ __html: description || '' }}></div>}
                                      color="#fff"
                                      zIndex={9999}
                                      overlayClassName="page-title-tooltip"
                                  >
                                      <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 13 }}/>
                                  </Tooltip>
                            </span>
                        }
                        <span className="group-item-icon-wrapper">{title}</span>
                    </div>
                </div>
            </div>
            <Space size={5} className="table-header-cell-buttons">
                {
                    isCompare ? null :
                        visibleFilterColumn  ? (
                            <Popover
                                content={<FilterPopover filters={filters} dataKey={dataKey} setVisible={setVisibleFilter} setVisibleFilterColumn={setVisibleFilterColumn} blockId={blockId} />}
                                trigger="click"
                                open={visibleFilter}
                                onOpenChange={setVisibleFilter}
                                placement="bottom"
                            >
                                <Button
                                    type={isFiltered ? 'primary' : 'default'}
                                    icon={<FilterOutlined />}
                                    size="small"
                                    title="Фильтровать по колонке"
                                    disabled={disabled}
                                />
                            </Popover>
                        ) : null
                }
                {
                    column.dataKey.startsWith('_') && isNumeric(String(column?.plan)) && visibleTargetMetric &&
                    <Button
                        type={visibleTargetMetric ? 'primary' : 'default'}
                        icon={customIcons.targetSimpleIcon({ style:{ fill:'white',marginTop:'4px' } })}
                        size="small"
                        title="Отобразить план"
                        disabled={disabled}
                        onClick={()=>{
                            setVisibleMorePopovers(false);
                            setVisibleTargetMetric(false);
                            const baseMetricName = column?.base_metric_name || dataKey;
                            if(visibleCumulativeChart){
                                setMetricsChecked({
                                    data: [
                                        ...metricsChecked.filter((item) => getMetricDataKey(item) !== dataKey),
                                        { dataKey: cumulativeDataKey, name: cumulativeDataKey, label: title, plan: null, base_metric_name: baseMetricName, metric_formula: column?.metric_formula, unit: column?.unit }
                                    ]
                                });
                            }else {
                                setMetricsChecked({
                                    data: [
                                        ...metricsChecked.filter((item) => getMetricDataKey(item) !== dataKey),
                                        { dataKey, name: dataKey, label: title, plan: null, base_metric_name: column?.base_metric_name, metric_formula: column?.metric_formula, unit: column?.unit }
                                    ]
                                });
                            }
                        }}
                    />
                }
                {
                    hideCumulativeMetric ? null :
                        visibleAbsoluteValue ? (
                            <Button
                                onClick={()=>{
                                    handlePercentages();
                                    setVisibleMorePopovers(false);
                                    setVisibleAbsoluteValue((prev)=>!prev);
                                }}
                                type={isPercentage}
                                icon={<PercentageOutlined/>}
                                size="small"
                                title="Абсолютные значения / Проценты"
                                disabled={isCompare || disabled}
                            />
                        ):null
                }
                {/* Кнопка линейного графика (всегда видна) */}
                {chartType === 'bubble' && !hideMetricButtons ? (
                    <Popover
                        content={<MetricsSelectModal dataKey={dataKey} setVisible={setVisibleMetricSelect}/>}
                        trigger="click"
                        open={visibleMetricSelect}
                        onOpenChange={setVisibleMetricSelect}
                        placement="topLeft"
                        title={'Выберите ось:'}
                    >
                        <Button
                            icon={<ChartLineIcon />}
                            size="small"
                            type={hasMetricByDataKey(metricsChecked, dataKey) ? 'primary' : 'default'}
                            title="Линейный график"
                            disabled={disabled}
                        />
                    </Popover>
                ) : !hideMetricButtons ? (
                    <Button
                        onClick={() => {
                            handleCheckedMetric();
                            setVisibleMorePopovers(false);
                            setVisibleCumulativeChart(false);
                        }}
                        icon={<ChartLineIcon />}
                        size="small"
                        type={checkedMetric && !currentMetricSettings.hideLine ? 'primary' : 'default'}
                        title="Линейный график"
                        disabled={disabled}
                    />
                ) : null}
                {/* Кнопка накопительного графика (видна только когда активна) */}
                {cumulativeCheckedMetric && chartType === 'line' && !hideCumulativeMetric && (
                    <Button
                        onClick={() => {
                            setCumulativeChart(false);
                            setMetricsChecked({ data: [...metricsChecked.filter((item) => getMetricDataKey(item) !== cumulativeDataKey)] });
                            setVisibleCumulativeChart((prev) => !prev);
                        }}
                        icon={<CumulativeChartIcon />}
                        size="small"
                        type="primary"
                        title="Накопительный график"
                        disabled={disabled}
                    />
                )}
                {/* Кнопка спарклайна — всегда в заголовке для быстрого включения (и дублируется в «…»);
                    с графиком не связана: мини-график в каждой ячейке столбца */}
                {(!hideMetricButtons || onlyTableSettings) && (
                    <Button
                        onClick={() => {
                            handleSparkline();
                            setVisibleMorePopovers(false);
                        }}
                        icon={<SparklineIcon />}
                        size="small"
                        type={isSparkline ? 'primary' : 'default'}
                        title={isSparkline
                            ? 'Спарклайн: выключить мини-график в ячейках'
                            : 'Спарклайн: мини-график по периодам в каждой ячейке столбца'}
                        disabled={isCompare || disabled}
                    />
                )}
                {
                    visiblePinFilter ? (
                        <Popover
                            content={<PinPopover dataKey={dataKey} setVisible={setVisiblePin} setFrozens={setFrozens} />}
                            trigger="click"
                            open={visiblePin}
                            onOpenChange={setVisiblePin}
                            placement="bottom"
                        >
                            <Button
                                icon={<PushpinOutlined />}
                                size="small"
                                title="Закрепить колонку"
                                disabled={disabled}
                            />
                        </Popover>
                    ): null
                }

{/* Блок-таблица дашборда: без «…» — в меню было бы всего две кнопки, и спарклайн уже стоит
    отдельно (Денис, 14.09). Рядом со спарклайном — только заливка по условиям */}
{onlyTableSettings && !isCustomMetric && (
                <Button
                    onClick={() => setVisibleCellFill(true)}
                    type={hasCellFill ? 'primary' : 'default'}
                    icon={<BgColorsOutlined />}
                    size="small"
                    title="Заливка ячеек по условиям «от – до»"
                    disabled={disabled}
                />
                )}

{!hideMetricButtons && (
                <Popover
                    content={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {/* Секция: Метрика */}
                            {column.dataKey.startsWith('_') && (
                                <div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4, fontWeight: 500 }}>Метрика</div>
                                    <Space size={4}>
                                        <Button
                                            type="default"
                                            icon={<EditOutlined />}
                                            size="small"
                                            title="Редактировать"
                                            disabled={disabled}
                                            onClick={() => {
                                                setVisibleMorePopovers(false);
                                                setEditMetricsState((prev) => ({ ...prev, visible: true }));
                                            }}
                                        />
                                        <Button
                                            type="default"
                                            icon={<CopyOutlined />}
                                            size="small"
                                            title="Дублировать"
                                            disabled={disabled}
                                            onClick={handleDuplicateButtonClick}
                                        />
                                        {isNumeric(String(column?.plan)) && (
                                            <Button
                                                type={visibleTargetMetric ? 'primary' : 'default'}
                                                icon={customIcons.targetSimpleIcon({ style: { marginTop: '4px' } })}
                                                size="small"
                                                title="Отобразить план"
                                                disabled={disabled}
                                                onClick={() => {
                                                    setVisibleMorePopovers(false);
                                                    setVisibleTargetMetric(true);
                                                    const baseMetricName = column?.base_metric_name || dataKey;
                                                    if (visibleCumulativeChart) {
                                                        setMetricsChecked({
                                                            data: [
                                                                ...metricsChecked.filter((item) => getMetricDataKey(item) !== dataKey),
                                                                { dataKey: cumulativeDataKey, name: cumulativeDataKey, label: title, plan: column?.plan, base_metric_name: baseMetricName, metric_formula: column?.metric_formula, unit: column?.unit }
                                                            ]
                                                        });
                                                    } else {
                                                        setMetricsChecked({
                                                            data: [
                                                                ...metricsChecked.filter((item) => getMetricDataKey(item) !== dataKey),
                                                                { dataKey, name: dataKey, label: title, plan: column?.plan, base_metric_name: column?.base_metric_name, metric_formula: column?.metric_formula, unit: column?.unit }
                                                            ]
                                                        });
                                                    }
                                                }}
                                            />
                                        )}
                                    </Space>
                                </div>
                            )}

                            {/* Секция: График */}
                            <div>
                                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4, fontWeight: 500 }}>График</div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    {chartType === 'bubble' ? (
                                        <Popover
                                            content={<MetricsSelectModal dataKey={dataKey} setVisible={setVisibleMetricSelect} />}
                                            trigger="click"
                                            open={visibleMetricSelect}
                                            onOpenChange={setVisibleMetricSelect}
                                            placement="topLeft"
                                            title={'Выберите ось:'}
                                        >
                                            <Button
                                                size="small"
                                                type={hasMetricByDataKey(metricsChecked, dataKey) ? 'primary' : 'default'}
                                                onClick={() => {
                                                    setVisibleMorePopovers(false);
                                                    setVisibleShowChart((prev) => !prev);
                                                }}
                                                disabled={disabled}
                                                className="popover-icon-btn"
                                            >
                                                <ChartLineIcon />
                                                <span style={{ fontSize: 10 }}>Линейный</span>
                                            </Button>
                                        </Popover>
                                    ) : (
                                        <Button
                                            onClick={() => {
                                                handleCheckedMetric();
                                                setVisibleMorePopovers(false);
                                                setVisibleShowChart((prev) => !prev);
                                            }}
                                            size="small"
                                            type={checkedMetric && !currentMetricSettings.hideLine ? 'primary' : 'default'}
                                            disabled={disabled}
                                            className="popover-icon-btn"
                                        >
                                            <ChartLineIcon />
                                            <span style={{ fontSize: 10 }}>Линейный</span>
                                        </Button>
                                    )}
                                    {chartType === 'line' && !hideCumulativeMetric && (
                                        <Button
                                            onClick={() => {
                                                if (!isChartVisible) {
                                                    localStorage.setItem('isChartVisible', `${!isChartVisible}`);
                                                    setChartVisible();
                                                }
                                                displayCumulativeChart();
                                                setVisibleMorePopovers(false);
                                                setVisibleCumulativeChart((prev) => !prev);
                                            }}
                                            size="small"
                                            type={cumulativeCheckedMetric ? 'primary' : 'default'}
                                            disabled={disabled}
                                            className="popover-icon-btn"
                                        >
                                            <CumulativeChartIcon />
                                            <span style={{ fontSize: 10 }}>Накопительный</span>
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Секция: Тренд и среднее */}
                            {(chartType === 'line' || chartType === 'area') && checkedMetric && (
                                <div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4, fontWeight: 500 }}>Тренд и среднее</div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <Button
                                            onClick={() => handleTrendTypeClick('linear')}
                                            size="small"
                                            type={isTrendButtonActive('linear') ? 'primary' : 'default'}
                                            disabled={disabled}
                                            className="popover-icon-btn"
                                        >
                                            <TrendLinearIcon />
                                            <span style={{ fontSize: 10 }}>Линейный</span>
                                        </Button>
                                        <Button
                                            onClick={() => handleTrendTypeClick('ma')}
                                            size="small"
                                            type={isTrendButtonActive('ma') ? 'primary' : 'default'}
                                            disabled={disabled}
                                            className="popover-icon-btn"
                                        >
                                            <TrendMaIcon />
                                            <span style={{ fontSize: 10 }}>MA</span>
                                        </Button>
                                        <Button
                                            onClick={() => handleTrendTypeClick('average')}
                                            size="small"
                                            type={isTrendButtonActive('average') ? 'primary' : 'default'}
                                            disabled={disabled}
                                            className="popover-icon-btn"
                                        >
                                            <TrendAvgIcon />
                                            <span style={{ fontSize: 10 }}>Средний</span>
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Секция: Дополнительно */}
                            <div>
                                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4, fontWeight: 500 }}>Дополнительно</div>
                                <Space size={4}>
                                    {!isCompare && (
                                        <Popover
                                            content={<FilterPopover filters={filters} dataKey={dataKey} setVisible={setVisibleFilter} setVisibleFilterColumn={setVisibleFilterColumn} blockId={blockId} />}
                                            trigger="click"
                                            open={visibleFilter}
                                            onOpenChange={setVisibleFilter}
                                            placement="bottom"
                                        >
                                            <Button
                                                type={isFiltered ? 'primary' : 'default'}
                                                icon={<FilterOutlined />}
                                                size="small"
                                                title="Фильтровать по колонке"
                                                disabled={disabled}
                                                onClick={() => setVisibleMorePopovers(false)}
                                            />
                                        </Popover>
                                    )}
                                    {!hideCumulativeMetric && (
                                        <Button
                                            onClick={() => {
                                                handlePercentages();
                                                setVisibleMorePopovers(false);
                                                setVisibleAbsoluteValue((prev) => !prev);
                                            }}
                                            type={isPercentage}
                                            icon={<PercentageOutlined />}
                                            size="small"
                                            title="Абсолютные значения / Проценты"
                                            disabled={isCompare || disabled}
                                        />
                                    )}
                                    <Button
                                        onClick={() => {
                                            handleSparkline();
                                            setVisibleMorePopovers(false);
                                        }}
                                        type={isSparkline ? 'primary' : 'default'}
                                        icon={<SparklineIcon />}
                                        size="small"
                                        title={isSparkline
                                            ? 'Спарклайн: выключить мини-график в ячейках'
                                            : 'Спарклайн: мини-график по периодам в каждой ячейке столбца'}
                                        disabled={isCompare || disabled}
                                    />
                                    {!isCustomMetric && (
                                        <Button
                                            onClick={() => {
                                                setVisibleMorePopovers(false);
                                                setVisibleCellFill(true);
                                            }}
                                            type={hasCellFill ? 'primary' : 'default'}
                                            icon={<BgColorsOutlined />}
                                            size="small"
                                            title="Заливка ячеек по условиям «от – до»"
                                            disabled={disabled}
                                        />
                                    )}
                                    <Popover
                                        content={<PinPopover dataKey={dataKey} setVisible={setVisiblePin} setFrozens={setFrozens} />}
                                        trigger="click"
                                        open={visiblePin}
                                        onOpenChange={setVisiblePin}
                                        placement="bottom"
                                    >
                                        <Button
                                            icon={<PushpinOutlined />}
                                            size="small"
                                            title="Закрепить колонку"
                                            disabled={disabled}
                                        />
                                    </Popover>
                                </Space>
                            </div>
                        </div>
                    }
                    trigger="click"
                    open={visibleMorePopovers}
                    onOpenChange={setVisibleMorePopovers}
                    placement="top"
                >
                    <Button
                        icon="..."
                        size="small"
                        disabled={disabled}
                    />
                </Popover>
                )}

            </Space>
            <FilterTag filter={filters[dataKey]} onClose={onRemoveFilter} />
            { editMetricsState.visible && <EditMetricsModal confirmCreatedMetricsRemoving={confirmCreatedMetricsRemoving} id={Number(dataKey.replace(/_(\d+?)_/g, '$1'))} visible={editMetricsState.visible} setEditState={setEditMetricsState} /> }
            { creatingMetricState.visible && <CreateFromExistingMetricModal id={Number(dataKey.replace(/_(\d+?)_/g, '$1'))} visible={ creatingMetricState.visible} setCreateState={setCreatingMetricState} /> }
            { visibleCellFill && (
                <CellFillModal
                    visible={visibleCellFill}
                    metricName={title}
                    value={cellFill}
                    onSave={handleCellFillSave}
                    onCancel={() => setVisibleCellFill(false)}
                />
            ) }
        </div>
    );
};


export default memo(HeaderCell);
